import type { ReactNode } from 'react';

interface DockedFooterProps {
    children: ReactNode;
}

/**
 * Actions pinned to the bottom of the viewport, with the page scrolling underneath.
 *
 * Lifted out of the check screen when the loyalty screen needed the same thing. It is not just a fixed
 * element: three details are what make content passing behind read as deliberate rather than clipped — a
 * translucent background with a blur, a hairline top border, and the scrim above it. Without the scrim a row
 * of text meets the footer edge mid-glyph and looks like a rendering fault.
 *
 * The scrolling content it sits over needs bottom padding of its own, or the last item is unreachable behind
 * this. There is no way to enforce that from here, which is why every caller does it explicitly.
 */
export function DockedFooter({ children }: DockedFooterProps) {
    return (
        <div className="pointer-events-none fixed inset-x-0 bottom-0">
            <div className="mx-auto w-full max-w-[var(--shell-max-width)]">
                <div className="h-8 bg-gradient-to-t from-background/90 to-transparent" />

                <footer className="pointer-events-auto border-t border-border bg-background/90 px-5 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] backdrop-blur-md">
                    {children}
                </footer>
            </div>
        </div>
    );
}
